import PropTypes from 'prop-types'
import DatePicker from 'react-datepicker'
import { DownloadIcon } from '@phosphor-icons/react'
import { fromZonedTime } from 'date-fns-tz'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { buildCsvFilename, downloadCsv } from '@/utils/file'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import api from '@/api/api'
import { useSnackbar } from '@/helpers/hooks/useSnackbar'

import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import Icon from '../../Icons'

const TicketFilters = ({
  toggleServiceFeeSettings,
  toggleSettings,
  setDateRange,
  dateRange,
  statusFilter,
  setStatusFilter,
  phoneOrLicensePlateFilter,
  setPhoneOrLicensePlateFilter,
  hasValetSettings,
}) => {
  const { t } = useTranslation(['parking-dashboard', 'common'])
  const building = useSelector(selectActiveBuilding)
  const [startDate, endDate] = dateRange
  const { displaySnackbar } = useSnackbar()

  const onCSVDownload = async () => {
    try {
      const requestData = {
        buildingId: building.id,
        startDate: startDate ? fromZonedTime(startDate, building.timezone).getTime() : undefined,
        endDate: endDate ? fromZonedTime(endDate, building.timezone).getTime() : undefined,
      }

      const { data } = await api.valetDownloadCSVReport(requestData)

      downloadCsv({
        blob: data,
        filename: buildCsvFilename({
          building: building?.name || null,
          page: 'parking',
          dateRange: [startDate, endDate],
          timezone: building?.timezone,
        }),
      })
    } catch ({ message }) {
      displaySnackbar(message || t('common:errors.generic'), true)
    }
  }

  return (
    <div className="parking-filters">
      <div className="status-filter-container">
        <div
          role="button"
          onClick={() => setStatusFilter('')}
          className="status-filter"
          style={{
            borderBottom: statusFilter === '' ? '2px #C2996D solid' : '2px transparent solid',
          }}
        >
          {t('parking-dashboard:filters.all')}
        </div>
        <div
          role="button"
          onClick={() => setStatusFilter('checked-in')}
          className="status-filter"
          style={{
            borderBottom:
              statusFilter === 'checked-in' ? '2px #C2996D solid' : '2px transparent solid',
          }}
        >
          {t('parking-dashboard:filters.checkedIn')}
        </div>
        <div
          role="button"
          onClick={() => setStatusFilter('checked-out')}
          className="status-filter"
          style={{
            borderBottom:
              statusFilter === 'checked-out' ? '2px #C2996D solid' : '2px transparent solid',
          }}
        >
          {t('parking-dashboard:filters.checkedOut')}
        </div>
      </div>
      <div className="filters-container">
        <div>
          <DatePicker
            className={'input'}
            selectsRange={true}
            placeholderText={t('parking-dashboard:selectTimeRange')}
            isClearable={true}
            maxDate={new Date()}
            onChange={setDateRange}
            startDate={startDate}
            endDate={endDate}
            popperClassName="parking-date-picker-popper"
          />
        </div>
        <div>
          <TextField
            placeholder={t('parking-dashboard:filters.searchPlaceholder')}
            style={{ width: '350px' }}
            value={phoneOrLicensePlateFilter}
            onChange={(e) => setPhoneOrLicensePlateFilter(e.target.value)}
            leftIcon={<Icon icon="search" />}
          />
        </div>
        <button
          className="button full-height is-black"
          disabled={!hasValetSettings}
          onClick={() => toggleServiceFeeSettings(true)}
        >
          {t('parking-dashboard:filters.setServiceFees')}
        </button>
        <button
          className="button full-height is-black"
          disabled={!hasValetSettings}
          onClick={() => toggleSettings(true)}
        >
          {t('parking-dashboard:filters.settings')}
        </button>
        <button className="button full-height is-black" onClick={onCSVDownload}>
          <DownloadIcon size={24} style={{ marginRight: '8px' }} />
          {t('parking-dashboard:filters.downloadCsv')}
        </button>
      </div>
    </div>
  )
}

TicketFilters.propTypes = {
  toggleServiceFeeSettings: PropTypes.func,
  toggleSettings: PropTypes.func,
  dateStartFilter: PropTypes.number,
  setDateStartFilter: PropTypes.func,
  dateEndFilter: PropTypes.number,
  setDateEndFilter: PropTypes.func,
  statusFilter: PropTypes.string,
  setStatusFilter: PropTypes.func,
  phoneOrLicensePlateFilter: PropTypes.string,
  setPhoneOrLicensePlateFilter: PropTypes.func,
  hasValetSettings: PropTypes.bool,
}

export default TicketFilters
